import { Form, Field, ErrorMessage, defineRule, configure } from 'vee-validate'
import { required, email, min, max, url, confirmed, digits } from '@vee-validate/rules'
import { localize } from '@vee-validate/i18n'
// import { Auth } from 'aws-amplify'

// Precomputed strings for fast character lookup
const SPECIAL_CHARS_STRING = '!@#$%^&*()_+-=[]{};\':\\"\\|,.<>/?'
const UPPERCASE_CHARS_STRING = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
const LOWERCASE_CHARS_STRING = 'abcdefghijklmnopqrstuvwxyz'
const DIGIT_CHARS_STRING = '0123456789'

// Define validation rule functions for testing access
const validationRules = {
  /**
   * Validates that a string contains at least one numeric digit (0-9)
   * @param {string} value - The value to validate
   * @returns {boolean} True if the value contains at least one digit, false otherwise
   * @example
   * // Returns true
   * containsNumber("password123")
   * // Returns false
   * containsNumber("password")
   */
  containsNumber: (value) => {
    if (!value || typeof value !== 'string') return false
    for (let i = 0; i < value.length; i++) {
      if (DIGIT_CHARS_STRING.indexOf(value[i]) !== -1) return true
    }
    return false
  },

  /**
   * Validates that a string contains at least one special character
   * Special characters include: !@#$%^&*()_+-=[]{}|;':"\\,.<>/?
   * SECURITY: Uses string indexOf to prevent ReDoS attacks and optimize performance
   * @param {string} value - The value to validate
   * @returns {boolean} True if the value contains at least one special character, false otherwise
   * @example
   * // Returns true
   * containsSpecialCharacter("password!")
   * // Returns false
   * containsSpecialCharacter("password123")
   */
  containsSpecialCharacter: (value) => {
    if (!value || typeof value !== 'string') return false
    for (let i = 0; i < value.length; i++) {
      if (SPECIAL_CHARS_STRING.indexOf(value[i]) !== -1) return true
    }
    return false
  },

  /**
   * Validates that a string contains at least one uppercase letter (A-Z)
   * @param {string} value - The value to validate
   * @returns {boolean} True if the value contains at least one uppercase letter, false otherwise
   * @example
   * // Returns true
   * containsUppercase("Password")
   * // Returns false
   * containsUppercase("password")
   */
  containsUppercase: (value) => {
    if (!value || typeof value !== 'string') return false
    for (let i = 0; i < value.length; i++) {
      if (UPPERCASE_CHARS_STRING.indexOf(value[i]) !== -1) return true
    }
    return false
  },

  /**
   * Validates that a string contains at least one lowercase letter (a-z)
   * @param {string} value - The value to validate
   * @returns {boolean} True if the value contains at least one lowercase letter, false otherwise
   * @example
   * // Returns true
   * containsLowercase("Password")
   * // Returns false
   * containsLowercase("PASSWORD")
   */
  containsLowercase: (value) => {
    if (!value || typeof value !== 'string') return false
    for (let i = 0; i < value.length; i++) {
      if (LOWERCASE_CHARS_STRING.indexOf(value[i]) !== -1) return true
    }
    return false
  },

  /**
   * Validates that a new password is different from the old password
   * This rule helps enforce password change policies by preventing users from reusing their current password
   *
   * Security features:
   * - Null/undefined safety checks (fails validation if either is null/undefined)
   * - Type safety checks (fails validation if either is not a string)
   * - Whitespace normalization and validation (fails if either becomes empty after trimming)
   * - Case-sensitive comparison (maintains password security)
   * - Constant-time comparison resistant to timing attacks (including length-based timing attacks)
   * - Fixed-length padding to prevent length-based timing analysis
   *
   * @param {string} value - The new password value to validate
   * @param {string[]} args - Array containing the old password as the first element
   * @returns {boolean} True if the new password is different from the old password, false if they are the same or invalid
   */
  differentFromOldPassword: (value, [oldPassword]) => {
    // Handle null/undefined cases and type safety - these should fail validation
    if (!value || !oldPassword || typeof value !== 'string' || typeof oldPassword !== 'string') {
      return false // Fail validation if either is null/undefined or not a string
    }

    // Normalize both passwords by trimming whitespace
    const normalizedNewPassword = value.trim()
    const normalizedOldPassword = oldPassword.trim()

    // Handle empty strings after trimming - these should fail validation
    if (!normalizedNewPassword || !normalizedOldPassword) {
      return false // Fail validation if either is empty after trimming
    }

    // TIMING ATTACK MITIGATION: Use fixed-length padding to prevent length-based timing analysis
    // Pad both strings to a fixed maximum length (256 chars) to eliminate timing differences
    const FIXED_LENGTH = 256
    const paddedNewPassword = normalizedNewPassword.padEnd(FIXED_LENGTH, '\0')
    const paddedOldPassword = normalizedOldPassword.padEnd(FIXED_LENGTH, '\0')

    // Constant-time comparison to prevent timing attacks
    // Always compare the full fixed length regardless of actual password lengths
    let result = 0
    for (let i = 0; i < FIXED_LENGTH; i++) {
      result |= paddedNewPassword.charCodeAt(i) ^ paddedOldPassword.charCodeAt(i)
    }

    // Return true if passwords are different (result !== 0)
    return result !== 0
  }
}

// Expose validation rules for testing
if (typeof window !== 'undefined') {
  window.VeeValidateRules = validationRules
  window.differentFromOldPassword = validationRules.differentFromOldPassword
}

export default (app) => {
  defineRule('required', required)
  defineRule('email', email)
  defineRule('min', min)
  defineRule('max', max)
  defineRule('length', (value) => {
    if (!value || value.trim().length < 36 || value.trim().length > 36) {
      return false
    }
    return true
  })
  defineRule('url', url)
  defineRule('digits', digits)
  defineRule('password_mismatch', confirmed)

  // Register our custom validation rules
  defineRule('containsNumber', validationRules.containsNumber)
  defineRule('containsSpecialCharacter', validationRules.containsSpecialCharacter)
  defineRule('containsUppercase', validationRules.containsUppercase)
  defineRule('containsLowercase', validationRules.containsLowercase)
  defineRule('differentFromOldPassword', validationRules.differentFromOldPassword)

  /**
   * Validates that a date is within a reasonable range (between 100 years ago and today)
   * Useful for birth dates and other date fields that should be within human lifespan
   * @param {string|Date} value - The date value to validate (can be string or Date object)
   * @returns {boolean} True if the date is within the valid range, false otherwise
   * @example
   * // Returns true for a date 30 years ago
   * validDate("1994-01-01")
   * // Returns false for a date 150 years ago
   * validDate("1874-01-01")
   * // Returns false for future dates
   * validDate("2050-01-01")
   */
  defineRule('validDate', (value) => {
    const hundredYearsAgo = new Date(new Date().setFullYear(new Date().getFullYear() - 100))
    const now = new Date()
    value = new Date(value)
    if (value >= hundredYearsAgo && value <= now) {
      // Pass
      return true
    } else {
      // Fail
      return false
    }
  })

  /**
   * Validates that a value is unique (typically for usernames/emails)
   * Currently returns true (disabled) but can be extended to check against a database or API
   * @param {string} value - The value to check for uniqueness
   * @param {string[]|Object} args - Arguments containing exclusions or additional parameters
   * @param {string} [args.excluding] - A value to exclude from uniqueness check (useful for updates)
   * @returns {Promise<boolean>} True if the value is unique or excluded, false if already taken
   * @example
   * // Usage in validation rules: "unique" or "unique:excludedValue"
   * // Currently always returns true (validation disabled)
   * await unique("newuser@example.com", [])
   * // With exclusion (useful for profile updates)
   * await unique("user@example.com", ["user@example.com"]) // returns true
   * @todo Implement actual uniqueness checking against backend API
   */
  defineRule('unique', async (value, args) => {
    let excluding
    if (Array.isArray(args)) {
      [excluding] = args
    } else {
      ({ excluding } = args)
    }
    if (value === excluding) return true
    // try {
    //   await Auth.signIn(value, '0')
    // } catch (error) {
    //   const code = error.code
    //   console.log(error)
    //   switch (code) {
    //     case 'UserNotFoundException':
    //       console.log('UserNotFoundException :>> ')
    //       return true
    //     default:
    //       console.log('UserFound :>> ')
    //       return false
    //   }
    // }
    return true
  })

  configure({
    generateMessage: localize('en', {
      messages: {
        required: '{field} is required',
        email: '{field} must be a valid email',
        min: '{field} must be a minimum of 0:{min} characters',
        max: '{field} must be a maximum of 0:{min} characters',
        length: '{field} must be a 0:{length} characters long',
        unique: '{field} is already taken',
        url: '{field} must be a valid URL',
        digits: '{field} must be numeric',
        containsNumber: '{field} must contain at least 1 number',
        containsSpecialCharacter: '{field} must contain at least 1 special character',
        containsUppercase: '{field} must contain at least 1 uppercase letter',
        containsLowercase: '{field} must contain at least 1 lowercase letter',
        differentFromOldPassword: '{field} must be different from the old password',
        validDate: '{field} must be between today and a hundred years ago'
      }
    })
  })

  app.component('VeeForm', Form)
  app.component('VeeField', Field)
  app.component('VeeErrorMessage', ErrorMessage)
}
